import { useState, useEffect, useCallback, useRef } from 'react';
import { Customer } from '../types';
import apiClient from '../utils/api';

export interface CustomerState {
  customer: Customer | null;
  loading: boolean;
  error: string | null;
  isEditing: boolean;
}

export interface CustomerOperations {
  updateCustomer: (updates: Partial<Customer>) => Promise<boolean>;
  addTag: (tag: string) => Promise<boolean>;
  removeTag: (tag: string) => Promise<boolean>;
  updateTags: (tags: string[]) => Promise<boolean>;
  saveNotes: (notes: string) => Promise<boolean>;
  refreshCustomer: () => Promise<void>;
  startEditing: () => void;
  cancelEditing: () => void;
  saveEditing: (updates: Partial<Customer>) => Promise<boolean>;
}

export interface UseCustomerConfig {
  customerId: string | null;
  autoLoad?: boolean;
  onUpdate?: (customer: Customer) => void;
  onError?: (error: string) => void;
}

export const useCustomer = (config: UseCustomerConfig): CustomerState & CustomerOperations => {
  const { customerId, autoLoad = true, onUpdate, onError } = config;

  const [state, setState] = useState<CustomerState>({
    customer: null,
    loading: false,
    error: null,
    isEditing: false,
  });

  const [originalCustomer, setOriginalCustomer] = useState<Customer | null>(null);
  const requestSeqRef = useRef(0); // 直近の要求シーケンス
  const lastRequestedIdRef = useRef<string | null>(null); // 直近に要求したID

  // コールバックをRefで保持（依存配列に入れない）
  const onUpdateRef = useRef(onUpdate);
  const onErrorRef = useRef(onError);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  // 顧客情報の読み込み
  const loadCustomer = useCallback(async (id: string) => {
    try {
      const seq = ++requestSeqRef.current;
      lastRequestedIdRef.current = id;
      setState(prev => ({ ...prev, loading: true, error: null }));
      const response = await apiClient.getCustomer(id);
      if (seq !== requestSeqRef.current || lastRequestedIdRef.current !== id) {
        return null;
      }
      if (response.success && response.data) {
        const normalized: Customer = {
          id: response.data.id,
          name: response.data.name,
          email: response.data.email,
          phone: response.data.phone,
          city: response.data.city,
          membershipLevel: response.data.membershipLevel,
          lastSeen: new Date(response.data.lastSeen),
          totalOrders: response.data.totalOrders,
          customTags: response.data.customTags || [],
        };
        setState(prev => ({ ...prev, customer: normalized, loading: false }));
        onUpdateRef.current?.(normalized);
        return normalized;
      } else {
        throw new Error(response.message || '顧客情報の取得に失敗しました');
      }
    } catch (error) {
      const isLatest = lastRequestedIdRef.current === customerId;
      const errorMessage = error instanceof Error ? error.message : '顧客情報の読み込みに失敗しました';
      if (isLatest) {
        setState(prev => ({ 
          ...prev, 
          error: errorMessage, 
          loading: false 
        }));
        onErrorRef.current?.(errorMessage);
      }
      return null;
    }
  }, [customerId]);

  // 顧客情報の更新
  const updateCustomer = useCallback(async (updates: Partial<Customer>): Promise<boolean> => {
    if (!state.customer) return false;

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const response = await apiClient.updateCustomer(state.customer.id, updates);
      if (response.success && response.data) {
        const normalized: Customer = {
          id: response.data.id,
          name: response.data.name,
          email: response.data.email,
          phone: response.data.phone,
          city: response.data.city,
          membershipLevel: response.data.membershipLevel,
          lastSeen: new Date(response.data.lastSeen),
          totalOrders: response.data.totalOrders,
          customTags: response.data.customTags || [],
        };
        setState(prev => ({ ...prev, customer: normalized, loading: false }));
        onUpdate?.(normalized);
        return true;
      }
      throw new Error(response.message || '顧客情報の更新に失敗しました');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '顧客情報の更新に失敗しました';
      setState(prev => ({ 
        ...prev, 
        error: errorMessage, 
        loading: false 
      }));
      onError?.(errorMessage);
      return false;
    }
  }, [state.customer, onUpdate, onError]);

  // タグ追加
  const addTag = useCallback(async (tag: string): Promise<boolean> => {
    if (!state.customer || !tag.trim()) return false;

    const normalizedTag = tag.trim();
    if (state.customer.customTags.includes(normalizedTag)) {
      return true; // 既に存在する場合は成功として扱う
    }

    const newTags = [...state.customer.customTags, normalizedTag];
    return await updateCustomer({ customTags: newTags });
  }, [state.customer, updateCustomer]);

  // タグ削除
  const removeTag = useCallback(async (tag: string): Promise<boolean> => {
    if (!state.customer) return false;

    const newTags = state.customer.customTags.filter(t => t !== tag);
    return await updateCustomer({ customTags: newTags });
  }, [state.customer, updateCustomer]);

  // タグ一括更新
  const updateTags = useCallback(async (tags: string[]): Promise<boolean> => {
    if (!state.customer) return false;
    const uniqueTags = Array.from(new Set(tags.filter(tag => tag.trim())));
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const response = await apiClient.updateCustomerTags(state.customer.id, uniqueTags);
      if (response.success && response.data) {
        setState(prev => ({ ...prev, customer: { ...prev.customer!, customTags: response.data.customTags || [] }, loading: false }));
        return true;
      }
      throw new Error(response.message || 'タグ更新に失敗しました');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'タグ更新に失敗しました';
      setState(prev => ({ ...prev, error: errorMessage, loading: false }));
      onError?.(errorMessage);
      return false;
    }
  }, [state.customer, updateCustomer]);

  // メモ保存
  const saveNotes = useCallback(async (notes: string): Promise<boolean> => {
    if (!state.customer) return false;
    try {
      const response = await apiClient.saveCustomerNotes(state.customer.id, notes);
      if (response.success) return true;
      throw new Error(response.message || 'メモ保存に失敗しました');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'メモ保存に失敗しました';
      setState(prev => ({ ...prev, error: errorMessage }));
      onError?.(errorMessage);
      return false;
    }
  }, [state.customer]);

  // 顧客情報の再読み込み
  const refreshCustomer = useCallback(async (): Promise<void> => {
    if (customerId) {
      await loadCustomer(customerId);
    }
  }, [customerId, loadCustomer]);

  // 編集モード開始
  const startEditing = useCallback(() => {
    if (state.customer) {
      setOriginalCustomer({ ...state.customer });
      setState(prev => ({ ...prev, isEditing: true }));
    }
  }, [state.customer]);

  // 編集キャンセル
  const cancelEditing = useCallback(() => {
    if (originalCustomer) {
      setState(prev => ({ 
        ...prev, 
        customer: originalCustomer, 
        isEditing: false 
      }));
      setOriginalCustomer(null);
    }
  }, [originalCustomer]);

  // 編集保存
  const saveEditing = useCallback(async (updates: Partial<Customer>): Promise<boolean> => {
    const success = await updateCustomer(updates);
    if (success) {
      setState(prev => ({ ...prev, isEditing: false }));
      setOriginalCustomer(null);
    }
    return success;
  }, [updateCustomer]);

  // 初期読み込み
  useEffect(() => {
    // 選択変更時は直前の顧客名が一瞬表示されないように即座にリセット
    setState(prev => ({ ...prev, customer: null, error: null }));
    if (customerId && autoLoad) {
      loadCustomer(customerId);
    } else {
      // IDが無効になった場合は要求シーケンスを進め、以降の古いレスポンスを無効化
      requestSeqRef.current++;
      lastRequestedIdRef.current = null;
    }
  }, [customerId, autoLoad]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // 状態
    ...state,
    
    // 操作
    updateCustomer,
    addTag,
    removeTag,
    updateTags,
    saveNotes,
    refreshCustomer,
    startEditing,
    cancelEditing,
    saveEditing,
  };
};

// ユーティリティ関数

function generateCustomerName(id: string): string {
  const names = [
    '田中太郎', '佐藤花子', '鈴木一郎', '高橋美咲', '渡辺健太',
    '伊藤真美', '山田和也', '中村千夏', '小林大輔', '加藤優子',
    '吉田雄二', '山本麻衣', '佐々木翔', '松本彩香', '井上博'
  ];
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return names[hash % names.length];
}

function generatePhoneNumber(id: string): string {
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const base = 1000000000 + (hash % 900000000);
  const phone = base.toString();
  return `090-${phone.slice(3, 7)}-${phone.slice(7, 11)}`;
}

function getRandomCity(): string {
  const cities = [
    '東京, Japan', '大阪, Japan', '名古屋, Japan', '横浜, Japan', 
    '札幌, Japan', '福岡, Japan', '神戸, Japan', '京都, Japan',
    '広島, Japan', '仙台, Japan'
  ];
  return cities[Math.floor(Math.random() * cities.length)];
}

function getRandomMembershipLevel(): 'regular' | 'vip' | 'premium' {
  const levels: ('regular' | 'vip' | 'premium')[] = ['regular', 'regular', 'regular', 'premium', 'vip'];
  return levels[Math.floor(Math.random() * levels.length)];
}

function getRandomTags(): string[] {
  const allTags = [
    '新規顧客', 'VIP', 'リピーター', '要注意', '優良顧客',
    '技術サポート', '営業案件', '解約検討', 'アップグレード候補',
    '問い合わせ多数', '迅速対応希望', '丁寧対応希望'
  ];
  
  const tagCount = Math.floor(Math.random() * 4) + 1; // 1-4個のタグ
  const selectedTags: string[] = [];
  
  for (let i = 0; i < tagCount; i++) {
    const remainingTags = allTags.filter(tag => !selectedTags.includes(tag));
    if (remainingTags.length > 0) {
      const randomTag = remainingTags[Math.floor(Math.random() * remainingTags.length)];
      selectedTags.push(randomTag);
    }
  }
  
  return selectedTags;
}

export default useCustomer;
